import React, { useState, useEffect } from 'react';
import { Calculator, ArrowRight, TrendingDown, FileDown, CheckCircle2, Minus, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Control that supports slider + typed number + +/- buttons.
const Field = ({ label, value, setValue, min, max, step, prefix = '', suffix = '', testid }) => {
  const round = (n) => Math.round(n * 100) / 100;
  const clamp = (n) => round(Math.min(max, Math.max(min, n)));
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);

  // Keep the visible text in sync when the value changes from sliders / buttons.
  useEffect(() => { if (!focused) setDraft(String(value)); }, [value, focused]);

  const commit = (raw) => {
    const n = parseFloat(raw);
    setValue(isNaN(n) ? min : clamp(n));
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <label className="text-sm font-medium text-[#003456]">{label}</label>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => setValue(clamp(value - step))} className="h-7 w-7 rounded-md border border-slate-300 text-[#003456] hover:bg-slate-100 flex items-center justify-center" aria-label={`Decrease ${label}`} data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></button>
          <div className="flex items-center rounded-md border border-slate-300 px-2">
            {prefix && <span className="text-sm text-[#B87333] font-bold">{prefix}</span>}
            <input
              type="number" value={draft} min={min} max={max} step={step}
              onFocus={() => setFocused(true)}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => { setFocused(false); commit(draft); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              className="w-12 text-center text-sm font-bold text-[#B87333] py-1 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`${testid}-input`} />
            {suffix && <span className="text-xs text-slate-500">{suffix.trim()}</span>}
          </div>
          <button type="button" onClick={() => setValue(clamp(value + step))} className="h-7 w-7 rounded-md border border-slate-300 text-[#003456] hover:bg-slate-100 flex items-center justify-center" aria-label={`Increase ${label}`} data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></button>
        </div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => setValue(parseFloat(e.target.value))} className="w-full accent-[#CE8946]" data-testid={testid} />
    </div>
  );
};

// Simple "cost of a day down" estimator, batches/day x value/batch x days offline.
export const DowntimeCalculator = ({ onEstimate }) => {
  const [cycleHours, setCycleHours] = useState(36);
  const [valuePerBatch, setValuePerBatch] = useState(600);
  const [daysDown, setDaysDown] = useState(10);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const USABLE_HRS_YR = 24 * 365 * 0.85; // ~85% usable uptime
  const batchesPerYear = Math.round(USABLE_HRS_YR / cycleHours);
  const perDay = (24 / cycleHours) * valuePerBatch;
  const total = perDay * daysDown;

  useEffect(() => {
    if (onEstimate) onEstimate({ total, perDay, cycleHours, valuePerBatch, daysDown, batchesPerYear });
  }, [total, perDay, cycleHours, valuePerBatch, daysDown, batchesPerYear, onEstimate]);

  const download = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter your email to get the free download.');
      return;
    }
    const DOWNTIME_PDF = 'https://customer-assets-wrfwihn1.emergentagent.net/job_9e1fb140-210f-412f-9430-9687aa3be1b0/artifacts/ximt138g_LLDowntimeEconomics-2026.pdf';
    try {
      await axios.post(`${API_URL}/api/leads/document`, {
        email,
        document_title: 'When the Machine Stops: The Cost of Downtime',
        download_url: DOWNTIME_PDF,
        category: 'case-study',
      });
    } catch (e) { /* non-blocking */ }
    window.open(DOWNTIME_PDF, '_blank', 'noopener');
    setSent(true);
    toast.success('Unlocked! We\'ve also emailed your link.');
  };

  return (
    <section id="downtime" className="py-20 section-padding bg-[#F1F5F9] scroll-mt-24" data-testid="downtime-calculator">
      <div className="max-w-5xl mx-auto">
        <div className="max-w-2xl mb-10">
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">What's downtime really costing you?</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3 flex items-center gap-3">
            <Calculator className="h-8 w-8 text-[#CE8946]" /> Downtime cost calculator
          </h2>
          <p className="text-slate-600 mt-3">Slide in your numbers to see roughly what a stalled machine is costing your business.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 space-y-7">
            <Field label="Batch cycle length" value={cycleHours} setValue={setCycleHours} min={3} max={60} step={3} suffix=" hrs" testid="calc-hours" />
            <Field label="Value per batch" value={valuePerBatch} setValue={setValuePerBatch} min={100} max={3000} step={50} prefix="$" testid="calc-value" />
            <Field label="Days machine is down" value={daysDown} setValue={setDaysDown} min={1} max={60} step={1} suffix=" days" testid="calc-days" />
          </div>

          <div className="bg-[#003456] rounded-2xl p-8 flex flex-col justify-center text-center">
            <span className="inline-flex items-center gap-2 justify-center text-[#CE8946] text-xs font-bold uppercase tracking-[0.2em]">
              <TrendingDown className="h-4 w-4" /> Estimated lost revenue
            </span>
            <p className="text-5xl sm:text-6xl font-extrabold text-white mt-4" data-testid="calc-total">{fmt(total)}</p>
            <p className="text-white/60 mt-3">≈ <span className="text-white font-semibold">{fmt(perDay)}</span> every day offline</p>
            <p className="text-white/40 text-xs mt-1">A {cycleHours}h cycle ≈ {batchesPerYear.toLocaleString()} batches/yr at ~85% uptime</p>
            <p className="text-white/40 text-xs mt-4">Rough estimate, your real numbers may vary. Doesn't include spoiled product or rush fees.</p>
            <a href="#diagnose" className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3.5 rounded-md transition-colors mt-6" data-testid="calc-cta">
              Get back online <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="case-study-download">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#003456] flex items-center gap-2"><FileDown className="h-5 w-5 text-[#CE8946]" /> Free downtime economics whitepaper</h3>
            <p className="text-slate-600 text-sm mt-1">Get our 2026 whitepaper on what downtime really costs a growing freeze dry business, and how a rebuild pays for itself. Drop your email to unlock the PDF.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {sent ? (
              <p className="text-sm font-semibold text-[#003456] flex items-center gap-2" data-testid="case-study-sent"><CheckCircle2 className="h-5 w-5 text-[#CE8946]" /> Thanks, check your inbox for your copy.</p>
            ) : (
              <>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email *" className="px-4 py-2.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="case-study-email" />
                <button onClick={download} className="inline-flex items-center justify-center gap-2 bg-[#003456] hover:bg-[#023F63] text-white font-bold px-6 py-2.5 rounded-md transition-colors whitespace-nowrap" data-testid="case-study-download-btn"><FileDown className="h-4 w-4" /> Download</button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DowntimeCalculator;
